import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HStack, VStack } from '~/components/layout/Stack';
import Text from '~/components/typographies/Text';
import IconStarlight from '~/icons/icon-starlight.svg?react';
import styles from './PayrollLoadingModal.module.scss';

interface PayrollLoadingModalProps {
  fileName: string;
  estimatedSeconds?: number;
  isComplete?: boolean;
  onComplete?: () => void;
}

const ESTIMATED_SECONDS = 30;
const COMPLETION_HOLD_MS = 1000;

type StepStatus = 'done' | 'active' | 'pending';

type StepLabelKey = 'ocr_loading_steps.preparation' | 'ocr_loading_steps.recognition' | 'ocr_loading_steps.validation';

const getSteps = (progress: number): { labelKey: StepLabelKey; status: StepStatus }[] => [
  {
    labelKey: 'ocr_loading_steps.preparation' as const,
    status: progress >= 33 ? 'done' : progress > 0 ? 'active' : 'pending',
  },
  {
    labelKey: 'ocr_loading_steps.recognition' as const,
    status: progress >= 80 ? 'done' : progress >= 33 ? 'active' : 'pending',
  },
  {
    labelKey: 'ocr_loading_steps.validation' as const,
    status: progress > 99 ? 'done' : progress >= 80 ? 'active' : 'pending',
  },
];

const CheckIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3.5 8L6.5 11L12.5 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SpinnerIcon: React.FC = () => (
  <svg
    className={styles.spinnerSvg}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="10" cy="10" r="8" stroke="#E0E2FF" strokeWidth="2" />
    <path d="M10 2C14.4183 2 18 5.58172 18 10" stroke="#5B6CF9" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const StepIcon: React.FC<{ status: StepStatus }> = ({ status }) => {
  if (status === 'done') {
    return (
      <span className={styles.stepIconDone}>
        <CheckIcon />
      </span>
    );
  }
  if (status === 'active') {
    return (
      <span className={styles.stepIconActive}>
        <SpinnerIcon />
      </span>
    );
  }
  return <span className={styles.stepIconPending} />;
};

export const PayrollLoadingModal = ({
  fileName,
  estimatedSeconds = ESTIMATED_SECONDS,
  isComplete = false,
  onComplete,
}: PayrollLoadingModalProps) => {
  const { t } = useTranslation('bookkeeping', {
    keyPrefix: 'general_bookkeeping.non_electronic_documents.payroll_registration',
  });
  const [progress, setProgress] = useState(0);
  const startTimeRef = useRef(Date.now());
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (isComplete) return;

    let frame = 0;
    const tick = () => {
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      const ratio = Math.min(1, elapsed / estimatedSeconds);
      const p = ratio < 0.8 ? (ratio * 95) / 0.8 : 95 + (ratio - 0.8) * (4 / 0.2);
      setProgress((prev) => Math.max(prev, Math.min(99, p)));
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [estimatedSeconds, isComplete]);

  useEffect(() => {
    if (!isComplete) return;

    setProgress(100);
    const timer = window.setTimeout(() => onCompleteRef.current?.(), COMPLETION_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [isComplete]);

  const elapsed = (Date.now() - startTimeRef.current) / 1000;
  const remaining = isComplete ? 0 : Math.max(0, Math.round(estimatedSeconds - elapsed));
  const steps = getSteps(progress);

  return (
    <div className={styles.overlay}>
      <div className={styles.card}>
        <HStack gap={2} className={styles.upsell}>
          <div className={styles.aiIcon}>
            <IconStarlight />
          </div>
          <VStack>
            <Text variant="body2" weight="semibold">
              {t('ocr_upsell_title')}
            </Text>
            <Text variant="caption" weight="medium">
              {t('ocr_upsell_subtitle')}
            </Text>
          </VStack>
        </HStack>

        <VStack gap={3} className={styles.cardContent}>
          <VStack gap={0.5} className={styles.minWidthZero}>
            <Text variant="subtitle2">{t('ocr_loading_title')}</Text>
            <Text variant="caption" weight="semibold" secondaryColor ellipsis>
              {fileName}
            </Text>
          </VStack>

          <VStack gap={1}>
            <HStack justify="space-between" align="end">
              <Text variant="title3" weight="semibold" tabularNums>
                {Math.round(progress)}%
              </Text>
              <Text variant="body2" weight="medium" secondaryColor>
                {t('ocr_loading_time_prefix')} {'~'}
                {remaining}
                {'s'}
              </Text>
            </HStack>
            <div className={styles.progressTrack}>
              <div className={styles.progressFill} style={{ width: `${progress}%` }} />
            </div>
          </VStack>

          <VStack gap={2.5}>
            {steps.map((step) => (
              <HStack key={step.labelKey} gap={2} align="center">
                <StepIcon status={step.status} />
                {step.status === 'done' ? (
                  <Text variant="body2" weight="semibold" className={styles.textViolet600}>
                    {t(step.labelKey)}
                  </Text>
                ) : step.status === 'active' ? (
                  <Text variant="body2" weight="semibold">
                    {t(step.labelKey)}
                  </Text>
                ) : (
                  <Text variant="body2" weight="regular" secondaryColor>
                    {t(step.labelKey)}
                  </Text>
                )}
              </HStack>
            ))}
          </VStack>
        </VStack>
      </div>
    </div>
  );
};
